import React from 'react';
import { fmtMoney, fmtDate } from '../../utils/campaignFinanceUtils';
import CommitteeLink from '../committee/CommitteeLink';

interface RecipientContextProps {
  committee: string;
  /** TRACER id of the committee — the name opens the committee card. */
  committeeCoId?: string | null;
  recent: Array<{
    id: string;
    date: string;
    contributor: string;
    city?: string;
    amount: number;
    type?: string;
    current?: boolean;
  }>;
  cycle?: {
    cycle: string;
    total_raised: number;
    contribution_count: number;
  };
  currentContributionId?: string;
}

const BAR_CURRENT = 'var(--cf-out-deep)';     // primary-800
const BAR_CURRENT_END = 'var(--cf-out)'; // primary-600
const BAR_DEFAULT = 'var(--cf-bar)';     // primary-400
const BAR_DEFAULT_END = 'var(--cf-bar-end)'; // primary-200

const RecipientContext: React.FC<RecipientContextProps> = ({ committee, committeeCoId, recent, cycle, currentContributionId }) => {
  if (recent.length === 0 && !cycle) return null;

  const sorted = [...recent].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const maxAmt = Math.max(...recent.map(r => r.amount), 1);

  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-5 py-4 border-b border-border-light dark:border-border-dark">
        <div className="text-xs font-semibold text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider mb-1">
          Recipient Context
        </div>
        <div className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
          <CommitteeLink coId={committeeCoId} name={committee} className="text-primary-700 dark:text-primary-dark-400" />
        </div>
        {cycle && (
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-2">
            This committee has raised {fmtMoney(cycle.total_raised, { compact: true })} from{' '}
            {cycle.contribution_count.toLocaleString()} contributions during the {cycle.cycle} cycle.
          </p>
        )}
      </div>

      {/* Recent contributions */}
      <div className="p-5">
        <div className="text-xs font-semibold text-text-secondary dark:text-text-dark-secondary uppercase tracking-wider mb-3">
          Recent Contributions
        </div>
        <div className="space-y-2">
          {sorted.map(r => {
            const isCurrent = r.current || r.id === currentContributionId;
            const pct = maxAmt > 0 ? (r.amount / maxAmt) * 100 : 0;
            return (
              <div key={r.id} className={`rounded-lg p-3 ${isCurrent ? 'bg-primary-50 dark:bg-primary-dark-50' : ''}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary truncate">{r.contributor}</span>
                    {r.city && (
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0">{r.city}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary tabular-nums">{fmtDate(r.date)}</span>
                    <span className={`text-sm tabular-nums ${isCurrent ? 'font-bold text-primary-800 dark:text-primary-dark-400' : 'font-semibold text-text-primary dark:text-text-dark-primary'}`}>
                      {fmtMoney(r.amount)}
                    </span>
                  </div>
                </div>
                <div className="h-1.5 bg-background-tertiary dark:bg-background-dark-tertiary rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      background: isCurrent
                        ? `linear-gradient(90deg, ${BAR_CURRENT}, ${BAR_CURRENT_END})`
                        : `linear-gradient(90deg, ${BAR_DEFAULT}, ${BAR_DEFAULT_END})`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RecipientContext;
